/**
 * exportUtils.js
 * Excel (XLSX) en PDF export voor de speaker startlijst.
 * Gebruikt ExcelJS voor Excel en jsPDF + jspdf-autotable voor PDF.
 */

import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const HRE_BLAUW = '001a4d';
const HRE_GROEN = 'b4c002';

// ─── Helpers ────────────────────────────────────────────────────────────────

function uitslagenTekst(results) {
  if (!results?.length) return '';
  return results.map((r) => {
    const tijd = r.totaaltijd || r.tijdrit || '—';
    const plaats = r.plaats && r.plaats !== '.' ? `, ${r.plaats}e` : '';
    return `${r.year}: ${tijd}${plaats}`;
  }).join(' | ');
}

function rijDeelnemer(p, oldResults) {
  return {
    Startnr: 'TBD',
    AN: p.StartNr || '',
    Naam: p.Naam || '',
    Woonplaats: p.Woonplaats || '',
    Serie: p.Onderdeel || '',
    Categorie: p.Categorie || '',
    'Uitslagen HRE': uitslagenTekst(oldResults?.[p.Naam]),
  };
}

function rijONKTeam(team) {
  const d1 = team.deelnemer1 || {};
  const d2 = team.deelnemer2 || {};
  return {
    Teamnaam: team.teamnaam || '',
    Categorie: team.categorie || '',
    'Deelnemer 1': d1.naam || '',
    'Woonplaats 1': d1.woonplaats || '',
    'Deelnemer 2': d2.naam || '',
    'Woonplaats 2': d2.woonplaats || '',
    Notitie: team.opmerking || '',
  };
}

// ─── Excel export ────────────────────────────────────────────────────────────

function stijlKopRij(row) {
  row.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${HRE_BLAUW}` } };
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.alignment = { vertical: 'middle' };
  });
  row.height = 20;
}

function voegIndividueelToe(ws, deelnemers, oldResults) {
  const headers = ['Startnr', 'AN', 'Naam', 'Woonplaats', 'Serie', 'Categorie', 'Uitslagen HRE'];
  const kopRij = ws.addRow(headers);
  stijlKopRij(kopRij);

  ws.columns = [
    { key: 'Startnr', width: 8 },
    { key: 'AN', width: 8 },
    { key: 'Naam', width: 28 },
    { key: 'Woonplaats', width: 18 },
    { key: 'Serie', width: 30 },
    { key: 'Categorie', width: 12 },
    { key: 'Uitslagen HRE', width: 50 },
  ];

  deelnemers.forEach((p, i) => {
    const rij = ws.addRow(Object.values(rijDeelnemer(p, oldResults)));
    if (i % 2 === 1) {
      rij.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FF' } };
      });
    }
    // AN in lichtgrijs
    const anCell = rij.getCell(2);
    anCell.font = { color: { argb: 'FFAAAAAA' }, italic: true };
  });

  ws.autoFilter = { from: 'A1', to: `G1` };
}

function voegONKToe(ws, teams) {
  const headers = ['Teamnaam', 'Categorie', 'Deelnemer 1', 'Woonplaats 1', 'Deelnemer 2', 'Woonplaats 2', 'Notitie'];
  const kopRij = ws.addRow(headers);
  stijlKopRij(kopRij);

  ws.columns = [
    { key: 'Teamnaam', width: 25 },
    { key: 'Categorie', width: 14 },
    { key: 'Deelnemer 1', width: 24 },
    { key: 'Woonplaats 1', width: 16 },
    { key: 'Deelnemer 2', width: 24 },
    { key: 'Woonplaats 2', width: 16 },
    { key: 'Notitie', width: 40 },
  ];

  teams.forEach((team, i) => {
    const rij = ws.addRow(Object.values(rijONKTeam(team)));
    if (i % 2 === 1) {
      rij.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FF' } };
      });
    }
  });
}

export async function exporteerExcel({
  jaar,
  deelnemers,
  onkTeams,
  oldResults,
  geldigVanaf,
}) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'HRE Eureka';
  wb.created = new Date();

  const SERIES = [
    { key: 'classic', label: 'Klassieker', filter: (p) => (p.Onderdeel || '').toLowerCase().includes('klassieker') },
    { key: 'swimrun', label: 'Swimrun', filter: (p) => (p.Onderdeel || '').toLowerCase().includes('swimrun') && !(p.Onderdeel || '').toLowerCase().includes('onk') },
    { key: 'kids', label: 'Kids Rondje', filter: (p) => (p.Onderdeel || '').toLowerCase().includes('kids') },
  ];

  // Tab per serie (geen 'Alles' tabblad)
  SERIES.forEach(({ label, filter }) => {
    const ws = wb.addWorksheet(label);
    voegIndividueelToe(ws, deelnemers.filter(filter), oldResults);
  });

  // Tab: ONK
  if (onkTeams?.length) {
    const wsOnk = wb.addWorksheet('ONK Teams');
    voegONKToe(wsOnk, onkTeams);
  }

  // Tab: Info
  const wsInfo = wb.addWorksheet('Info');
  wsInfo.addRow(['HRE Eureka Startlijst Export']);
  wsInfo.addRow([`Jaar: ${jaar}`]);
  wsInfo.addRow([`Gegenereerd: ${new Date().toLocaleDateString('nl-NL')}`]);
  if (geldigVanaf) wsInfo.addRow([`Lijst bijgewerkt op: ${geldigVanaf}`]);
  wsInfo.addRow([`Totaal deelnemers: ${deelnemers.length}`]);
  if (onkTeams?.length) wsInfo.addRow([`ONK teams: ${onkTeams.length}`]);
  wsInfo.addRow([]);
  wsInfo.addRow(['Kolom uitleg:']);
  wsInfo.addRow(['Startnr', 'TBD = To Be Determined — definitief startnummer volgt later']);
  wsInfo.addRow(['AN', 'Aanmeldnummer — toegewezen bij inschrijving']);
  wsInfo.getColumn(1).width = 12;
  wsInfo.getColumn(2).width = 60;
  wsInfo.getRow(1).font = { bold: true, size: 14 };

  // Download
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const a = Object.assign(document.createElement('a'), {
    href: URL.createObjectURL(blob),
    download: `speakerlijst-hre-${jaar}.xlsx`,
  });
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
}

// ─── PDF export ───────────────────────────────────────────────────────────────

export function exporteerPdf({
  jaar,
  deelnemers,
  onkTeams,
  oldResults,
  geldigVanaf,
}) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const paginaBreedte = doc.internal.pageSize.getWidth();

  function voegKopToe(titel) {
    doc.setFillColor(0, 26, 77); // HRE blauw
    doc.rect(0, 0, paginaBreedte, 16, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`HRE ${jaar} — ${titel}`, 10, 11);
    if (geldigVanaf) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Bijgewerkt: ${geldigVanaf}`, paginaBreedte - 10, 11, { align: 'right' });
    }
    doc.setTextColor(0, 0, 0);
  }

  const SERIES = [
    { label: 'Klassieker', filter: (p) => (p.Onderdeel || '').toLowerCase().includes('klassieker') },
    { label: 'Swimrun', filter: (p) => (p.Onderdeel || '').toLowerCase().includes('swimrun') && !(p.Onderdeel || '').toLowerCase().includes('onk') },
    { label: 'Kids Rondje', filter: (p) => (p.Onderdeel || '').toLowerCase().includes('kids') },
  ];

  let eersteBlad = true;

  SERIES.forEach(({ label, filter }) => {
    const rijen = deelnemers.filter(filter);
    if (!rijen.length) return;
    if (!eersteBlad) doc.addPage();
    eersteBlad = false;
    voegKopToe(label);

    autoTable(doc, {
      startY: 20,
      head: [['TBD', 'AN', 'Naam', 'Woonplaats', 'Onderdeel', 'Cat.', 'Uitslagen HRE']],
      body: rijen.map((p) => [
        'TBD',
        p.StartNr || '',
        p.Naam || '',
        p.Woonplaats || '',
        p.Onderdeel || '',
        p.Categorie || '',
        uitslagenTekst(oldResults?.[p.Naam]),
      ]),
      headStyles: { fillColor: [0, 26, 77], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 249, 255] },
      columnStyles: {
        0: { cellWidth: 14 },  // TBD
        1: { cellWidth: 14, textColor: [180, 180, 180], fontStyle: 'italic' }, // AN
        2: { cellWidth: 45 },  // Naam
        3: { cellWidth: 28 },  // Woonplaats
        4: { cellWidth: 50 },  // Onderdeel
        5: { cellWidth: 14 },  // Cat
        6: { cellWidth: 'auto' }, // Uitslagen
      },
      styles: { fontSize: 8, cellPadding: 2 },
      margin: { top: 20 },
    });
  });

  // ONK pagina
  if (onkTeams?.length) {
    doc.addPage();
    voegKopToe('ONK Teams');
    autoTable(doc, {
      startY: 20,
      head: [['Teamnaam', 'Categorie', 'Deelnemer 1', 'Woonplaats 1', 'Deelnemer 2', 'Woonplaats 2', 'Notitie']],
      body: onkTeams.map((t) => [
        t.teamnaam || '',
        t.categorie || '',
        t.deelnemer1?.naam || '',
        t.deelnemer1?.woonplaats || '',
        t.deelnemer2?.naam || '',
        t.deelnemer2?.woonplaats || '',
        t.opmerking || '',
      ]),
      headStyles: { fillColor: [0, 26, 77], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 249, 255] },
      styles: { fontSize: 8, cellPadding: 2 },
      margin: { top: 20 },
    });
  }

  doc.save(`speakerlijst-hre-${jaar}.pdf`);
}
